import { FC, useState } from 'react';
import { Box, Center } from '@chakra-ui/react';

import { Scope } from 'common/hooks/useAuthenticatedAction/types';
import { isOldCardDesign } from 'common/paymentCards/utils';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import useToast from 'components/ToastProvider/useToast';
import { useActivatePhysicalPaymentCard } from 'features/PaymentCards/pages/PaymentCardList/components/PaymentCardSidebar/Activate/hooks/useActivatePhysicalPaymentCard';
import { StrongAuthDetailSidebar } from 'features/Team/pages/Requests/components/StrongAuthDetailSidebar';

import NavigationHeader from '../../../../../components/NavigationHeader';
import { CreditCard } from '../../../../../hooks/useCreditCards';
import ActivationSuccess from './ActivationSuccess';
import locales from './locales';
import * as S from './styles';
import TokenInput from './TokenInput';
import { getCardTokenAssetSrc } from './utils';

interface ActivateProps {
  creditCard: CreditCard;
}

const Activate: FC<ActivateProps> = ({ creditCard }) => {
  const { showToast } = useToast();
  const [publicToken, setPublicToken] = useState<string>('');

  const { creditCardId } = creditCard;
  const cardTokenAssetUrl = getCardTokenAssetSrc(creditCard);

  const [activate, { data, error, loading }] = useActivatePhysicalPaymentCard();

  const handleOnPasscodeSuccess = async (shortLivedToken: string) => {
    try {
      await activate({
        context: {
          headers: {
            'short-lived-token': shortLivedToken,
          },
        },
        variables: {
          input: {
            paymentCardId: creditCardId,
            token: publicToken,
          },
        },
      });
      showToast({ message: locales.successMessage, type: 'success' });
    } catch (e) {
      // Clear the public token if the activation fails. The error toast will
      // be handled directly because `activate` is using the `useErrorHandledMutation`
      // so no need here to have another toast.
      setPublicToken('');
    }
  };

  if (loading) {
    return (
      <Center height="100%">
        <Loader />
      </Center>
    );
  }

  if (data?.activatePhysicalPaymentCard && !error) {
    return (
      <ActivationSuccess
        creditCardId={creditCardId}
        pin={data.activatePhysicalPaymentCard.pin}
      />
    );
  }

  return (
    <StrongAuthDetailSidebar
      border="none"
      description={locales.strongAuthSidebarDescription}
      marginX={publicToken === '' ? undefined : '-space-24'}
      onPasscodeSuccess={handleOnPasscodeSuccess}
      padding="space-0"
      scope={Scope.BANK_PAYMENT_CARDS_WRITE}
    >
      {({ showPasscode }) => (
        <>
          <NavigationHeader backTo={`/cards/${creditCardId}`} />

          <Box marginBottom="space-32">
            <S.CardAssetContainer
              withAdjustedMargin={isOldCardDesign(creditCard)}
            >
              <S.CardImage
                alt={locales.tokenImageAltText}
                src={cardTokenAssetUrl}
              />
            </S.CardAssetContainer>
          </Box>

          <Text textAlign="center">{locales.title}</Text>

          <TokenInput
            onTokenFilled={(token) => {
              setPublicToken(token);
              showPasscode();
            }}
          />
        </>
      )}
    </StrongAuthDetailSidebar>
  );
};

export default Activate;
